import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    getNotifications,
    getUnreadNotificationsCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    hideNotification,
} from '../../../../../../helpers/notification-helper';
import { getNotificationVisual } from '../../../../../../helpers/notification-ui-helper';

export const NotificationsAction = ({ show: showProp, onToggle, onClose }) => {
    const { token } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const [internalShow, setInternalShow] = useState(false);
    const containerRef = useRef(null);
    const isControlled = typeof showProp === 'boolean';
    const show = isControlled ? showProp : internalShow;
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const closeMenu = useCallback(() => {
        if (isControlled) {
            onClose?.();
            return;
        }

        setInternalShow(false);
    }, [isControlled, onClose]);

    const toggleMenu = () => {
        if (isControlled) {
            onToggle?.();
            return;
        }

        setInternalShow((prev) => !prev);
    };

    const fetchUnreadCount = useCallback(async () => {
        if (!token) {
            return;
        }

        const response = await getUnreadNotificationsCount(token);
        if (response?.success) {
            setUnreadCount(Number(response?.data?.unread || 0));
        }
    }, [token]);

    const fetchNotifications = useCallback(async () => {
        if (!token) {
            return;
        }

        setLoading(true);
        const response = await getNotifications(token, { page: 1, limit: 10 });
        if (response?.success) {
            setNotifications(Array.isArray(response?.data?.notifications)
                ? response.data.notifications
                : []);
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                closeMenu();
            }
        };

        const handleClickOutside = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                closeMenu();
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [show, closeMenu]);

    useEffect(() => {
        fetchUnreadCount();
        fetchNotifications();

        const intervalId = setInterval(() => {
            fetchUnreadCount();
            fetchNotifications();
        }, 25000);

        return () => {
            clearInterval(intervalId);
        };
    }, [fetchNotifications, fetchUnreadCount]);

    const handleToggle = async () => {
        const nextShow = !show;
        toggleMenu();

        if (nextShow) {
            await Promise.all([fetchNotifications(), fetchUnreadCount()]);
        }
    };

    const handleMarkOneAsRead = async (notificationId) => {
        if (!notificationId || !token) {
            return;
        }

        const response = await markNotificationAsRead(token, notificationId);
        if (!response?.success) {
            return;
        }

        setNotifications((prev) => prev.map((item) => {
            if (item._id !== notificationId) {
                return item;
            }

            return {
                ...item,
                isRead: true,
            };
        }));
        setUnreadCount((prev) => Math.max(prev - 1, 0));
    };

    const handleMarkAllAsRead = async () => {
        if (!token) {
            return;
        }

        const response = await markAllNotificationsAsRead(token);
        if (!response?.success) {
            return;
        }

        setNotifications((prev) => prev.map((item) => ({
            ...item,
            isRead: true,
        })));
        setUnreadCount(0);
    };

    const handleNotificationClick = async (notification) => {
        if (!notification) {
            return;
        }

        if (!notification.isRead) {
            await handleMarkOneAsRead(notification._id);
        }

        const ticketId = notification?.ticket?._id
            || notification?.metadata?.ticketId
            || '';

        if (ticketId) {
            navigate(`/tasks/detail/${ticketId}`);
            closeMenu();
        }
    };

    const handleHideNotification = async (notificationId) => {
        if (!notificationId || !token) {
            return;
        }

        const response = await hideNotification(token, notificationId);
        if (!response?.success) {
            return;
        }

        setNotifications((prev) => {
            const selected = prev.find((item) => item._id === notificationId);
            if (selected && !selected.isRead) {
                setUnreadCount((current) => Math.max(current - 1, 0));
            }
            return prev.filter((item) => item._id !== notificationId);
        });
    };

    const formatTimeAgo = (dateValue) => {
        if (!dateValue) {
            return 'Ahora';
        }

        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) {
            return 'Ahora';
        }

        const diffMinutes = Math.max(Math.floor((Date.now() - date.getTime()) / (1000 * 60)), 0);
        if (diffMinutes < 1) return 'Ahora';
        if (diffMinutes < 60) return `${diffMinutes} min`;

        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours} h`;

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} d`;
    };

    const hasNotifications = useMemo(
        () => Array.isArray(notifications) && notifications.length > 0,
        [notifications],
    );

    return (
        <div ref={containerRef} className="dropdown nxl-h-item">
            <button type="button" className="nxl-head-link me-3 border-0 bg-transparent p-0 notifications-bell-trigger" data-bs-toggle="dropdown" data-bs-auto-close="outside" onClick={handleToggle}>
                <i className="feather-bell"></i>
                {unreadCount > 0 && (
                    <span className="badge bg-danger notifications-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>
            <div className={`dropdown-menu dropdown-menu-end nxl-h-dropdown nxl-notifications-menu ${show ? 'show' : ''}`}>
                <div className="d-flex justify-content-between align-items-center notifications-head">
                    <h6 className="fw-bold text-dark mb-0">Notificaciones</h6>
                    <button
                        type="button"
                        className="fs-11 text-success text-end ms-auto bg-transparent border-0"
                        data-bs-toggle="tooltip"
                        title="Marcar todas como leídas"
                        onClick={handleMarkAllAsRead}
                        disabled={unreadCount === 0}
                    >
                        <i className="feather-check"></i>
                        <span>Marcar todas</span>
                    </button>
                </div>
                <div className="notifications-list-scroll">
                {loading && !hasNotifications && (
                    <div className="notifications-item">
                        <div className="notifications-desc">
                            <div className="font-body">Cargando notificaciones...</div>
                        </div>
                    </div>
                )}

                {!loading && !hasNotifications && (
                    <div className="notifications-item">
                        <div className="notifications-desc">
                            <div className="font-body">No tienes notificaciones.</div>
                        </div>
                    </div>
                )}

                {hasNotifications && notifications.map((notification) => (
                    <div key={notification._id} className={`notifications-item notifications-item--clickable notification-tone notification-tone--${getNotificationVisual(notification.eventType).tone}`}>
                        <div className="notification-tone__icon">
                            <i className={getNotificationVisual(notification.eventType).icon}></i>
                        </div>
                        <div className="notifications-desc w-100">
                            <button
                                type="button"
                                className="font-body text-truncate-2-line bg-transparent border-0 p-0 text-start w-100"
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <span className={`badge bg-light text-${getNotificationVisual(notification.eventType).tone === 'neutral' ? 'secondary' : getNotificationVisual(notification.eventType).tone} me-2`}>{getNotificationVisual(notification.eventType).label}</span>
                                <span className="fw-semibold text-dark">{notification.title}</span>{' '}
                                {notification.message}
                            </button>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="notifications-date text-muted border-bottom border-bottom-dashed">
                                    {formatTimeAgo(notification.createdDate)}
                                </div>
                                <div className="d-flex align-items-center float-end gap-2">
                                    {!notification.isRead && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light p-1"
                                            data-bs-toggle="tooltip"
                                            title="Marcar como leída"
                                            onClick={() => handleMarkOneAsRead(notification._id)}
                                        >
                                            <i className="feather-check"></i>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light p-1 text-danger"
                                        title="Ocultar"
                                        onClick={() => handleHideNotification(notification._id)}
                                    >
                                        <i className="feather-x"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                </div>
                <div className="text-center notifications-footer">
                    <button
                        type="button"
                        className="fs-13 fw-semibold text-dark bg-transparent border-0"
                        onClick={() => {
                            navigate('/notifications');
                            closeMenu();
                        }}
                    >
                        Ver todas
                    </button>
                </div>
            </div>
        </div>
    )
}
